import { h as __h } from "preact";
import { Component, h } from "preact";
/** @import { TargetedMouseEvent } from "preact"; */

import { adminMachineResponse, adminMemberResponse } from "~/backend/validate.js";
import { paginatedResponse, query, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import * as msg from "~/lib/ws/msg.js";
/** @import WS from "~/lib/ws/ws.js"; */

import html from "~/util/html.js";
import { navigate } from "~/util/router.js";
import Title from "~/util/title.js";
import Avatar from "~/component/Avatar.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Loader from "~/component/Loader.js";
import WSListener from "~/component/WSListener.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import MappingsLock from "~/admin/component/MappingsLock.js";
import ObjectPage, { ObjectPageSection } from "~/admin/component/ObjectPage.js";
import Pagination from "~/admin/component/Pagination.js";
import Roles from "~/admin/lib/roles.js";
import ComputerTable from "~/admin/res/computer/ComputerTable.js";
import GroupChip from "~/admin/res/group/GroupChip.js";
import { changeRole } from "~/admin/res/member/actions/changeRole.js";
import { deleteMembers } from "~/admin/res/member/actions/deleteMembers.js";
import { manageGroups } from "~/admin/res/member/actions/manageGroups.js";
import util from "~/style/util.js";
/** @import { orgResponse, sessionEnterpriseResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {orgResponse} org
 * @property {sessionEnterpriseResponse} enterprise
 * @property {string} memberId
 * @property {WS} ws
 */
export default class MembersDetail extends Component {
  memberLookup = query(() => ({
    key: [`/ui/admin/orgs/${this.props.org.id}/members/${this.props.memberId}`],
    keepPreviousData: true
  }), {
    schema: response(adminMemberResponse)
  });
  render() {
    const member = this.memberLookup.data?.data;
    return [__h(Crumb, {}, member?.name ?? __h(Loader, {})), member && __h(Title, {
      title: member.name
    }), __h(Data, {
      loading: this.memberLookup.loading && !this.memberLookup.data,
      error: this.memberLookup.error,
      empty: Boolean(this.memberLookup.data) && !member
    }, member && this.renderPage(member))];
  }

  /** @param {adminMemberResponse} member */
  renderPage(member) {
    const locked = this.props.enterprise.is_group_sync_enabled;
    const canEditMembers = identity.permission(this.props.org.id, "members", "edit");
    const canEditGroups = identity.permission(this.props.org.id, "groups", "edit");
    return __h(ObjectPage, {
      backlink: {
        href: `/org/${this.props.org.id}/members`,
        name: "Members"
      },
      title: [__h(Avatar, {
        name: member.name,
        size: 22
      }), __h("span", {}, member.name)],
      desc: member.email,
      actions: [canEditGroups && __h(MappingsLock, {
        locked: locked
      }, __h(Button, {
        size: "small",
        disabled: locked,
        onClick: (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => manageGroups({
          anchor: event.currentTarget,
          placement: "bottom-end",
          orgId: this.props.org.id
        }, member)
      }, __h(Icon, {
        name: "users",
        size: 12
      }), "Manage groups")), canEditMembers && __h(MappingsLock, {
        locked: locked
      }, __h(Button, {
        size: "small",
        kind: "negative",
        disabled: locked,
        onClick: async () => {
          const res = await deleteMembers({
            orgId: this.props.org.id
          }, member.id);
          if (res) navigate(`/org/${this.props.org.id}/members`);
        }
      }, __h(Icon, {
        name: "trash",
        size: 12
      }), "Remove"))],
      sidebar: [__h(ObjectPageSection, {
        title: "Email"
      }, __h("span", {}, member.email)), __h(ObjectPageSection, {
        title: "Role",
        actions: canEditMembers && __h(MappingsLock, {
          locked: locked
        }, __h(Button, {
          size: "small",
          level: "tertiary",
          disabled: locked,
          onClick: (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => changeRole({
            anchor: event.currentTarget,
            placement: "bottom",
            orgId: this.props.org.id
          }, member)
        }, __h(Icon, {
          name: "pencil",
          size: 12
        }), "Edit"))
      }, __h("span", {}, Roles.label("org", member.role)))]
    }, __h(ObjectPageSection, {
      title: "Computers"
    }, __h("div", {
      "class": util.card
    }, __h(Computers, {
      org: this.props.org,
      user_id: this.props.memberId,
      ws: this.props.ws
    }))), __h(ObjectPageSection, {
      title: "Groups"
    }, member.groups.map(group => __h(GroupChip, {
      group: {
        ...group,
        org_id: this.props.org.id
      }
    }))));
  }
}

/**
 * @extends {Component<ComputersProps>}
 *
 * @typedef {object} ComputersProps
 * @property {orgResponse} org
 * @property {string} user_id
 * @property {WS} ws
 */
class Computers extends Component {
  paginate = new Pagination.State({
    size: 5,
    local: true
  });
  sort = ComputerTable.sort({
    local: true
  });
  machines = query(() => ({
    key: [`/ui/admin/orgs/${this.props.org.id}/machines`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      user_id: this.props.user_id
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(adminMachineResponse)
  });
  render() {
    return [h(ComputerTable, {
      orgId: this.props.org.id,
      paginate: this.paginate,
      sort: this.sort,
      query: this.machines,
      headers: h => h.filter(h => h.id !== "assignedTo"),
      footer: __h("a", {
        href: "/org/" + this.props.org.id + "/computers?user_id=" + this.props.user_id
      }, "Show/edit all results"),
      scroll: false
    }), __h(WSListener, {
      ws: this.props.ws,
      "for": [msg.ADMIN_MACHINE_REFRESH, msg.ADMIN_CONNECTION_REFRESH],
      on: () => this.machines.fetch()
    })];
  }
}